import { Action, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { createCareerMutation } from '@frontend/unhideschool/app/shared/models/graphql/workspace/create-career.mutation';
import { fetchCareersQuery } from '@frontend/unhideschool/app/shared/models/graphql/workspace/fetch-career.query';
import { GraphqlApiService } from '@frontend/unhideschool/app/api-gateway/services/graphql-api.service';
import {
  ICourseTrail,
  SpaceStateModel,
  defaultMembershipState,
} from './space.model';
import {
  CreateCareer,
  DeleteCareer,
  FetchCareers,
  UpdateCareer,
  UpdateMemberCareer,
  UpdateUserName,
  SearchUser,
  AddMember,
  FetchWorkSpace,
  FetchProgressCareer,
  FetchProgressCourse,
} from './space.action';
import { mutationUpdateUserNames } from '../models/graphiql/updateUser.mutation';
import { catchError, map, tap, throwError } from 'rxjs';
import { CourseAdminService } from '../../course-admin/services/course-admin.service';
import { updateCareerMutation } from '@frontend/unhideschool/app/shared/models/graphql/workspace/update-career.mutation';
import { updateMemberCareerMutation } from '@frontend/unhideschool/app/shared/models/graphql/workspace/update-member-career.mutation';
import { UserApiService } from '@frontend/unhideschool/app/shared/services/api-client/user-api.service';
import { SubscriptionMemberStates } from '@frontend/unhideschool/app/shared/models/subscription/subscription-members.model';
import { MeInfo, UNHUserProfile } from '@frontend/unhideschool/app/core/models';
import { UserSearchFoundUser } from '@frontend/unhideschool/app/shared/models/user-search.model';
import { UHQLApiService } from '@frontend/unhideschool/app/api-gateway/services/uhql-api.service';
import { AddSubscriptionMemberSchema } from '../../membership/models/schemas/add-subscription-member.schema';
import { asNumber } from '@frontend/unhideschool/app/helpers/utility-functions';
import { meAccessQuery } from '@frontend/unhideschool/app/shared/models/graphql/workspace/fetch-workspace.query';
import { StorageService } from '@frontend/unhideschool/app/api-gateway/services/storage.service';
import { LoggedUserStateService } from '@frontend/unhideschool/app/core/services/legacy/logged-user.state';
import { environment } from '@frontend/unhideschool/env';
import { MembershipSubscriptionMemberPatch } from '../../membership/actions/membership-subscription.actions';
import { progressCareerQuery } from '../models/graphiql/progressCareer.query';
import { progressCourseQuery } from '../models/graphiql/progressCourse.query';

@State<SpaceStateModel>({
  name: 'space',
  defaults: defaultMembershipState,
})
@Injectable()
export class SpaceState {
  constructor(
    private graph: GraphqlApiService,
    private cas: CourseAdminService,
    private uapis: UserApiService,
    private uhql: UHQLApiService,
    private storage: StorageService,
    private luss: LoggedUserStateService
  ) {}

  @Action(CreateCareer)
  async createCareer(ctx: StateContext<SpaceStateModel>, action: CreateCareer) {
    try {
      const uploadResponse = await this.updateImg(
        action.careerUpdatePayload.file
      );

      if (uploadResponse.status !== 'complete') {
        throw new Error('Upload incompleto');
      }

      const mediaitem = uploadResponse?.message?.data?.UploadMedia?.mediaitem;

      if (!mediaitem) {
        throw new Error('mediaitem');
      }

      const createCareerResponse = await this.graph
        .graphqlRequest(createCareerMutation, {
          workspaceid: action.careerUpdatePayload.workSpaceId,
          name: action.careerUpdatePayload.name,
          courseIds: action.careerUpdatePayload.courseIds,
          mediaitemid: Number(mediaitem.mediaitemid),
          active: true,
        })
        .toPromise();

      console.log(createCareerResponse, 'Resposta da criação da carreira');
    } catch (error) {
      console.error('Erro ao criar a carreira:', error);
    }
  }

  @Action(UpdateCareer)
  async updateCareer(ctx: StateContext<SpaceStateModel>, action: UpdateCareer) {
    try {
      const file = action.careerUpdatePayload.file;
      let mediaItemId = action.careerUpdatePayload.mediaitemid;

      if (file?.size) {
        const uploadResponse = await this.updateImg(file);

        if (uploadResponse.status !== 'complete') {
          throw new Error('Upload incompleto');
        }

        const mediaitem = uploadResponse?.message?.data?.UploadMedia?.mediaitem;

        if (!mediaitem) {
          throw new Error('mediaitem');
        }

        mediaItemId = mediaitem.mediaitemid;
      }

      const updateMemberCareer = await this.graph
        .graphqlRequest(updateCareerMutation, {
          name: action.careerUpdatePayload.name,
          courseIds: action.careerUpdatePayload.courseIds,
          mediaitemid: mediaItemId,
          careerid: action.careerUpdatePayload.careerId,
          active: true,
          positionCourseIds: action.careerUpdatePayload.courseIds?.map(
            (id, index) => {
              return { courseId: id, position: index + 1 };
            }
          ),
        })
        .toPromise();
    } catch (error) {
      console.error('Erro ao atualizar carreira:', error);
    }
  }

  @Action(UpdateMemberCareer)
  async updateMemberCareer(
    ctx: StateContext<SpaceStateModel>,
    action: UpdateMemberCareer
  ) {
    try {
      const updateMemberCareerResponse = await this.graph
        .graphqlRequest<'UpdateUserProfile', any>(updateMemberCareerMutation, {
          careerIds: action.careerIds,
          userId: action.userId,
        })
        .toPromise();

      const updatedCareers =
        updateMemberCareerResponse?.data?.UpdateUserProfile?.user?.careers ?? [];

      // A mutation devolve os cargos de todos os workspaces; a lista de membros
      // só exibe os do workspace atual, que são exatamente os que acabamos de
      // enviar. Assim o patch local fica igual ao que o fetch traria.
      const careers = updatedCareers
        .filter((c) => action.careerIds.includes(asNumber(c.careerId)))
        .map((c) => ({ id: asNumber(c.careerId), name: c.career?.name ?? '' }));

      ctx.dispatch(
        new MembershipSubscriptionMemberPatch(asNumber(action.userId), {
          careers,
        })
      );

      return careers;
    } catch (error) {
      console.error('Erro ao atualizar da carreira do membro:', error);
    }
  }

  @Action(UpdateUserName)
  async updateUserName(
    ctx: StateContext<SpaceStateModel>,
    action: UpdateUserName
  ) {
    try {
      const updateUserResponse = await this.graph
        .graphqlRequest<'UpdateUserProfile', any>(mutationUpdateUserNames, {
          userid: action.userId,
          username: action.username,
          alias: action.alias,
          fullname: action.fullname,
        })
        .toPromise();

      const user = updateUserResponse?.data?.UpdateUserProfile?.user;

      if (user) {
        ctx.dispatch(
          new MembershipSubscriptionMemberPatch(
            asNumber(user.uid ?? action.userId),
            {
              fullname: user.fullname,
              username: user.username,
              alias: user.alias,
            }
          )
        );
      }

      return updateUserResponse;
    } catch (error) {
      console.error('Erro ao atualizar nome do usuário:', error);
      throw error;
    }
  }

  @Action(DeleteCareer)
  async deleteCareer(ctx: StateContext<SpaceStateModel>, action: DeleteCareer) {
    try {
      const deleteCareerResponse = await this.graph
        .graphqlRequest(updateCareerMutation, {
          careerid: action.careerId,
          active: false,
        })
        .toPromise();

      console.log(deleteCareerResponse, 'Resposta da exclusão da carreira');
    } catch (error) {
      console.error('Erro ao excluir a carreira:', error);
    }
  }

  @Action(FetchCareers)
  fetchCareers(ctx: StateContext<SpaceStateModel>, action: FetchCareers) {
    ctx.patchState({
      careers: { courseTrail: [], isLoading: true },
    });

    return this.graph
      .graphqlRequest<'CourseTrail', ICourseTrail>(fetchCareersQuery, {
        workspaceId: action.workSpaceId,
      })
      .pipe(
        map((res) => res?.data?.CourseTrail?.['all']),
        tap((courseTrail) => {
          console.log(courseTrail, 'courseTrail');
          ctx.patchState({
            careers: { courseTrail: courseTrail, isLoading: false },
          });

          catchError((error) => {
            ctx.patchState({ careers: { courseTrail: [], isLoading: false } });
            return throwError(() => error);
          });
        })
      );
  }

  async updateImg(file: File) {
    return this.cas.uploadimage(file, 'careerthumb').toPromise();
  }

  @Action(AddMember)
  async addSubscriptionMember(
    ctx: StateContext<SpaceStateModel>,
    action: AddMember
  ) {
    const schema = new AddSubscriptionMemberSchema(
      asNumber(action.member.user.uid),
      asNumber(action.subscriptionid),
      action.member.subscriptionmemberstateid,
      action.member.subscriptionmembertypeid
    );

    const newUser = await this.uhql
      .uhqlRequest('CREATE', 'SUBSCRIPTION_MEMBERS', schema)
      .toPromise();

    console.log(newUser, 'new');
  }

  @Action(SearchUser)
  async searchUser(ctx, action) {
    this.uapis.getUsersByEmail(action.email).subscribe((res) => {
      ctx.patchState({
        membersSearched: [],
      });

      const foundUsers = res.dict.resource;

      if (foundUsers.length === 0) return;

      console.log(res, 'res');

      const foundUsersModel = res.dict.resource.map((u: UNHUserProfile) => {
        const isSubscriptionMember = action.subscriptionMembers.some((me) => {
          const memberstate = me.subscriptionmemberstate.internalname;
          const isActiveMember =
            memberstate === SubscriptionMemberStates.ACCEPTED ||
            memberstate === SubscriptionMemberStates.INVITED;
          return me.user.email === u.email && isActiveMember;
        });

        return new UserSearchFoundUser(u, isSubscriptionMember);
      });

      ctx.patchState({
        membersSearched: foundUsersModel,
      });
    });
  }

  @Action(FetchWorkSpace)
  async fetchWorkSpace() {
    if (this.luss.isLogged) {
      return this.graph.graphqlRequest<any, any>(meAccessQuery).pipe(
        map((res) => res?.data?.me),
        tap((me) => {
          const unhuser = environment.storagekeys.unhuser;
          const storageSave = this.storage.getItem(unhuser);

          this.luss.updateHasAccessToSpecialPosts(me?.hasAccessToSpecialPosts);

          if (storageSave && me?.user?.workspaces?.length) {
            storageSave.user.workspaces = me?.user?.workspaces;
            storageSave.user.hasAccessToSpecialPosts =
              me?.hasAccessToSpecialPosts;
            this.storage.setItem(unhuser, storageSave);

            this.luss.updateLoggedUserStatesWorkSpace(me?.user?.workspaces);
          }

          catchError((error) => {
            console.log(error, 'catch erro');
            return throwError(() => error);
          });
        })
      );
    } else {
      return true;
    }
  }

  @Action(FetchProgressCareer)
  async fetchProgressCareer(ctx, action: FetchProgressCareer) {
    ctx.patchState({
      progressCareers: [],
    });

    try {
      const request = await this.graph
        .graphqlRequest<any, any>(progressCareerQuery, {
          uid: action.uid,
          subscriptionMemberId: action.subscriptionMemberId,
          workspaceId: action.workspaceId,
        })
        .toPromise();
      console.log(request, 'request');

      ctx.patchState({
        progressCareers:
          request?.data?.SubscriptionMember?.item?.user?.careers ?? [],
      });

      return request?.data?.SubscriptionMember?.item?.user?.careers ?? []
    } catch (error) {
      console.error('Erro ao buscar o progresso da carreira:', error);
      ctx.patchState({
        progressCareers: [],
      });
    }
  }
  
  @Action(FetchProgressCourse)
  async FetchProgressCourse(ctx, action: FetchProgressCourse) {
    ctx.patchState({
      progressCourses: [],
    });

    try {
      const request = await this.graph
        .graphqlRequest<any, any>(progressCourseQuery, {
          uid: action.uid,
          careerId: action.careerId,
        })
        .toPromise();
      console.log(request, 'request');

      ctx.patchState({
        progressCourses:
          request?.data?.CourseTrail?.item?.courses?.items ?? [],
      });
    } catch (error) {
      console.error('Erro ao buscar o progresso da carreira:', error);
      ctx.patchState({
        progressCourses: [],
      });
    }
  }
}
